import React, { useState, useEffect } from 'react';
import { orderAPI } from '../../services/api';
import { addDays, todayKey } from '../../utils/date';
import '../Customers/Customers.css';

function ScheduleOrderModal({ isOpen, onClose, order, onScheduled }) {
  const [loading, setLoading] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [customDate, setCustomDate] = useState('');
  const [dateCubeSummary, setDateCubeSummary] = useState({});
  const [error, setError] = useState(null);

  // Generate next 15 days starting from today. The arithmetic is done
  // on the DATE, so the list starts on the day the operator is looking
  // at - not on the UTC day, which is yesterday for half of every NZ day.
  const generateNext15Days = () => {
    const days = [];
    const today = todayKey();
    for (let i = 0; i < 15; i++) {
      days.push(addDays(today, i));
    }
    return days;
  };

  const next15Days = generateNext15Days();

  useEffect(() => {
    if (isOpen && order) {
      fetchCubeSummaries();
    }
  }, [isOpen, order]);

  const fetchCubeSummaries = async () => {
    setLoading(true);
    try {
      const summaries = {};
      
      // Fetch scheduled orders for each of the next 15 days
      const promises = next15Days.map(async (date) => {
        try {
          const orders = await orderAPI.getUnallocatedByScheduleDate(date);
          // Calculate total cubes for this date
          let totalCubes = 0;
          const suburbs = new Set();
          
          if (orders && orders.length > 0) {
            orders.forEach((order) => {
              if (order.order_lines && order.order_lines.length > 0) {
                order.order_lines.forEach((line) => {
                  totalCubes += (line.size || 1) * (line.qty || 1);
                });
              }
              // Extract suburb from delivery address
              if (order.delivery_address) {
                const suburb = extractSuburb(order.delivery_address);
                if (suburb) {
                  suburbs.add(suburb);
                }
              }
            });
          }
          summaries[date] = { 
            count: orders?.length || 0, 
            cubes: totalCubes,
            suburbs: Array.from(suburbs)
          };
        } catch (err) {
          console.error(`Error fetching orders for ${date}:`, err);
          summaries[date] = { count: 0, cubes: 0, suburbs: [] };
        }
      });

      await Promise.all(promises);
      setDateCubeSummary(summaries);
    } catch (err) {
      console.error('Error fetching cube summaries:', err);
    } finally {
      setLoading(false);
    }
  };

  // Extract suburb from a New Zealand delivery address
  // Typical format: "123 Street Name, Suburb Name, Auckland" or "123 Street Name, Suburb Name"
  const extractSuburb = (address) => {
    if (!address) return null;
    
    // Split by comma and get the part before the city (if city is mentioned)
    const parts = address.split(',').map(p => p.trim());
    
    // If we have at least 2 parts, the suburb is typically the second-to-last part
    // (last part is usually city like "Auckland", "Wellington", etc.)
    if (parts.length >= 2) {
      // get part directly after the address rather than 2nd to last parts[parts.length - 2];
      const possibleSuburb = parts[1];  
      // Check if it looks like a suburb name (not a city name we know)
      const cityNames = ['auckland', 'wellington', 'christchurch', 'hampshire', 'papakura', 'manukau', 'north shore', 'waitakere', 'rodney', 'helensville', 'warkworth'];
      const lowerSuburb = possibleSuburb.toLowerCase();
      if (!cityNames.includes(lowerSuburb)) {
        return possibleSuburb;
      }
    }
    
    // If only 2 parts, assume the first is street and second could be suburb/city combined
    if (parts.length === 2) {
      return parts[1];
    }
    
    // Fallback: return the last part if it doesn't look like a city
    const lastPart = parts[parts.length - 1];
    const cityNames = ['auckland', 'wellington', 'christchurch'];
    if (lastPart && !cityNames.includes(lastPart.toLowerCase())) {
      return lastPart;
    }
    
    return null;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]}`;
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setCustomDate(''); // Clear custom date when selecting from list
  };

  const handleCustomDateChange = (e) => {
    const date = e.target.value;
    setCustomDate(date);
    setSelectedDate(''); // Clear list selection when using custom date
  };

  const handleSchedule = async () => {
    const dateToUse = customDate || selectedDate;
    
    if (!dateToUse) {
      setError('Please select a date or enter a custom date');
      return;
    }

    setScheduling(true);
    setError(null);

    try {
      await orderAPI.update(order.id, {
        schedule_date: dateToUse
      });
      
      if (onScheduled) {
        onScheduled();
      }
      onClose();
    } catch (err) {
      console.error('Error scheduling order:', err);
      setError(err.response?.data?.message || 'Failed to schedule order');
    } finally {
      setScheduling(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1000px' }}>
        <div className="modal-header">
          <h2>Schedule Order</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {order && (
            <div className="order-info" style={{ marginBottom: '20px', padding: '10px', background: '#f8f9fa', borderRadius: '4px' }}>
              <strong>Order #{order.id}</strong> - {order.customer?.fullname || 'Unknown Customer'}
              {order.order_external_reference && (
                <span> ({order.order_external_reference})</span>
              )}
              {order.delivery_address && (
                <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
                  📍 {order.delivery_address}
                </div>
              )}
              {order.note && (
                <div style={{ marginTop: '8px', fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
                  💬 {order.note}
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="error-message" style={{ marginBottom: '15px' }}>{error}</div>
          )}

          <div className="schedule-dates-list" style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: '600' }}>Select a Date</label>
            {loading ? (
              <div className="loading-small">
                <div className="spinner-small"></div>
                <span>Loading dates...</span>
              </div>
            ) : (
              <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                {next15Days.map((date) => {
                  const summary = dateCubeSummary[date] || { count: 0, cubes: 0, suburbs: [] };
                  const isSelected = selectedDate === date;
                  
                  return (
                    <div
                      key={date}
                      onClick={() => handleDateSelect(date)}
                      style={{
                        padding: '12px 15px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #eee',
                        background: isSelected ? '#e7f3ff' : 'white',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <input
                            type="radio"
                            checked={isSelected}
                            onChange={() => handleDateSelect(date)}
                            style={{ margin: 0 }}
                          />
                          <span style={{ fontWeight: '500' }}>{formatDate(date)}</span>
                        </div>
                        <div style={{ textAlign: 'right', fontSize: '14px', color: '#666' }}>
                          <span>{summary.count} order{summary.count !== 1 ? 's' : ''}</span>
                          <span style={{ margin: '0 8px' }}>•</span>
                          <span
                            style={{
                              padding: '2px 6px',
                              borderRadius: '3px',
                              fontWeight: '500',
                              background: summary.cubes > 90 ? '#dc3545' : summary.cubes > 80 ? '#ffc107' : 'transparent',
                              color: summary.cubes > 90 || summary.cubes > 80 ? 'white' : 'inherit'
                            }}
                          >
                            {summary.cubes} cube{summary.cubes !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                      {summary.suburbs && summary.suburbs.length > 0 && (
                        <div style={{ marginTop: '6px', marginLeft: '26px', fontSize: '13px', color: '#888' }}>
                          <span style={{ color: '#666' }}>Suburbs: </span>
                          {summary.suburbs.map((suburb, idx) => {
                            const isMatch = order && extractSuburb(order.delivery_address) === suburb;
                            return (
                              <span
                                key={idx}
                                style={{
                                  background: isMatch ? '#28a745' : '#e2e2e2',
                                  color: isMatch ? 'white' : 'inherit',
                                  padding: isMatch ? '1px 4px' : '0',
                                  borderRadius: isMatch ? '2px' : '0',
                                  marginRight: '4px'
                                }}
                              >
                                {suburb}
                              </span>
                            );
                          })}
                          {summary.suburbs.length > 50  && ` +${summary.suburbs.length - 50} more`}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="custom-date-input" style={{ borderTop: '1px solid #ddd', paddingTop: '15px' }}>
            <label htmlFor="custom-schedule-date" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Or Set Custom Date
            </label>
            <input
              type="date"
              id="custom-schedule-date"
              value={customDate}
              onChange={handleCustomDateChange}
              className="form-control"
              min={next15Days[0]}
              style={{ width: '100%' }}
            />
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={scheduling}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSchedule}
            disabled={scheduling || (!selectedDate && !customDate)}
          >
            {scheduling ? 'Scheduling...' : 'Schedule'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ScheduleOrderModal;
